import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import MovieCard from '@/components/base/MovieCard';
import EmptyState from '@/components/base/EmptyState';
import { movies } from '@/mocks/movies';
import FilterBar, { defaultFilters, type Filters } from './components/FilterBar';

const PAGE_SIZE = 10;

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<Filters>({
    ...defaultFilters,
    genre: searchParams.get('genre') || 'all',
    director: searchParams.get('director') || 'all',
    sort: searchParams.get('sort') || 'default',
    year: searchParams.get('year') || 'all',
  });
  const [tag, setTag] = useState(searchParams.get('tag') || '');
  const keyword = searchParams.get('q') || '';
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [filters, tag, keyword]);

  const filtered = useMemo(() => {
    let list = movies.filter((m) => {
      if (filters.year !== 'all' && m.year !== Number(filters.year)) return false;
      if (filters.director !== 'all' && m.director !== filters.director) return false;
      if (filters.genre !== 'all' && !m.genres.includes(filters.genre)) return false;
      if (filters.rating !== 'all' && m.rating < Number(filters.rating)) return false;
      if (tag && !m.tags.includes(tag)) return false;
      if (keyword) {
        const hay = [m.title, m.titleEn, m.director, ...m.genres, ...m.tags].join(' ').toLowerCase();
        if (!hay.includes(keyword.toLowerCase())) return false;
      }
      return true;
    });

    if (filters.sort === 'year') list = [...list].sort((a, b) => b.year - a.year);
    else if (filters.sort === 'rating') list = [...list].sort((a, b) => b.rating - a.rating);

    return list;
  }, [filters, tag, keyword]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const applyFilters = (patch: Partial<Filters>) => setFilters((f) => ({ ...f, ...patch }));

  const resetAll = () => {
    setFilters(defaultFilters);
    setTag('');
    setSearchParams({});
  };

  useEffect(() => {
    const parts: string[] = [];
    if (filters.year !== 'all') parts.push(`${filters.year}年上映`);
    if (filters.director !== 'all') parts.push(`${filters.director}导演`);
    if (filters.genre !== 'all') parts.push(`${filters.genre}类型`);
    if (tag) parts.push(tag);
    const suffix = parts.join(' - ');
    const pageSuffix = page > 1 ? ` - 第 ${page} 页` : '';
    document.title = suffix
      ? `韩国剧情电影分类 - ${suffix}${pageSuffix} - 韩国理论片`
      : `韩国剧情电影分类${pageSuffix} - 韩国理论片`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        'content',
        `韩国理论片电影分类页，${suffix ? `当前筛选：${suffix}。` : ''}收录韩国剧情与理论电影，支持按年份、导演、评分与类型筛选。`,
      );
    }
  }, [filters, tag, page]);

  return (
    <SiteLayout sidebar>
      <Breadcrumb items={[{ label: '电影分类' }]} />

      <header className="mb-6">
        <p className="font-label text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">
          Film Catalog
        </p>
        <h1 className="mt-2 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">
          电影分类列表
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          筛选范围为韩国剧情电影及理论电影。可按年份、导演、评分区间与影片类型组合筛选，快速定位你想深入了解的作品。
        </p>

        {(tag || keyword) && (
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">当前检索：</span>
            {tag && (
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-100 px-3 py-1 font-label text-xs text-accent-900">
                标签「{tag}」
                <button
                  type="button"
                  onClick={() => setTag('')}
                  className="ml-1 cursor-pointer"
                  aria-label="移除标签筛选"
                >
                  <i className="ri-close-line" aria-hidden="true" />
                </button>
              </span>
            )}
            {keyword && (
              <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 font-label text-xs text-secondary-900">
                关键词「{keyword}」
              </span>
            )}
          </div>
        )}
      </header>

      <FilterBar filters={filters} onChange={applyFilters} onReset={resetAll} resultCount={filtered.length} />

      <div className="mt-6">
        {pageItems.length === 0 ? (
          <EmptyState
            icon="ri-film-line"
            title="没有找到符合筛选条件的影片"
            description="当前组合条件下暂时没有匹配的韩国剧情或理论电影。可以尝试放宽评分区间、切换年份或导演，或直接重置全部筛选条件。"
            actionLabel="重置筛选条件"
            onAction={resetAll}
          />
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
              {pageItems.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
            <p className="mt-4 text-center text-xs text-foreground-400">
              第 {page} / {totalPages} 页 · 每页 {PAGE_SIZE} 条 · 共 {filtered.length} 部影片（前端演示数据）
            </p>
          </>
        )}
      </div>
    </SiteLayout>
  );
}